import axios from 'axios';
import type {
  DecodedTokenView,
  BlacklistStatusView,
  RefreshHistoryView,
  DecodeRequest,
} from '../types/tokenInspector.types';
import { attachAuthInterceptors } from '../../_shared/auth/authInterceptor';

const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE ?? '/api/eraf',
  headers: { 'Content-Type': 'application/json' },
});
attachAuthInterceptors(apiClient);

function getStoredToken(): string | null {
  return (
    localStorage.getItem('access_token') ??
    sessionStorage.getItem('access_token')
  );
}

// Request interceptor — attach Bearer token
apiClient.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor — unwrap ApiResponse<T>
apiClient.interceptors.response.use(
  (response) => {
    const d = response.data as Record<string, unknown> | undefined;
    if (d && typeof d === 'object' && 'success' in d) {
      if (d.success === true) {
        return { ...response, data: d['data'] };
      }
      const err = d['error'] as Record<string, unknown> | undefined;
      return Promise.reject({
        code: err?.['code'] ?? `HTTP-${response.status}`,
        message: err?.['message'] ?? 'Unknown error',
        traceId: (err?.['traceId'] as string) ?? response.headers?.['x-trace-id'],
      });
    }
    return response;
  },
  (error) => {
    const errData = error.response?.data?.error as Record<string, unknown> | undefined;
    const code = errData?.['code'] ?? `HTTP-${error.response?.status ?? 0}`;
    const message =
      (errData?.['message'] as string) ??
      (error.response?.data?.message as string) ??
      error.message ??
      'Network error';
    const traceId = (error.response?.headers?.['x-trace-id'] as string) ?? undefined;
    return Promise.reject({ code, message, traceId });
  }
);

export const tokenInspectorApi = {
  /** GET /api/eraf/dev/token-inspector/current */
  getCurrent: (): Promise<DecodedTokenView> =>
    apiClient
      .get<unknown, { data: DecodedTokenView }>('/dev/token-inspector/current')
      .then((res) => res.data),

  /** POST /api/eraf/dev/token-inspector/decode */
  decode: (req: DecodeRequest): Promise<DecodedTokenView> =>
    apiClient
      .post<unknown, { data: DecodedTokenView }>('/dev/token-inspector/decode', req)
      .then((res) => res.data),

  /** GET /api/eraf/dev/token-inspector/blacklist?jti=... */
  getBlacklistStatus: (jti: string): Promise<BlacklistStatusView> =>
    apiClient
      .get<unknown, { data: BlacklistStatusView }>(
        `/dev/token-inspector/blacklist?jti=${encodeURIComponent(jti)}`
      )
      .then((res) => res.data),

  /** GET /api/eraf/dev/token-inspector/refresh-history?userId=...&size=20 */
  getRefreshHistory: (userId: number): Promise<RefreshHistoryView> =>
    apiClient
      .get<unknown, { data: RefreshHistoryView }>(
        `/dev/token-inspector/refresh-history?userId=${userId}&size=20`
      )
      .then((res) => res.data),
};
